import { TFunction } from "i18next";
import * as yup from "yup";
import {
  getBillingAddressCountriesOptions,
  getCountriesOptions,
  getCountryByCode,
} from "utils/countries";
import { CreditCardBillingAddressDto } from "types/api/creditCard";
import {
  CreditCardBillingAddressValues,
  CreditCardDetailsFormValues,
  CreditCardFormValues,
} from "types/creditCard";

const DEFAULT_COUNTRY_CODE = "US";

const getBillingAddressCountryOption = (
  locale = "en",
  countryCode = DEFAULT_COUNTRY_CODE,
) => {
  const countries = getBillingAddressCountriesOptions(locale);

  return (
    getCountryByCode(countries, countryCode) ||
    getCountryByCode(countries, DEFAULT_COUNTRY_CODE) ||
    null
  );
};

export type CreditCardBrand = "visa" | "mastercard" | "amex";

export const getDefaultCreditCardDetailsValues =
  (): CreditCardDetailsFormValues => ({
    name: "",
    number: "",
    expiration: "",
    cvv: "",
  });

export const getRestorableCreditCardDetailsValues = (
  values?: Partial<CreditCardDetailsFormValues> | null,
): Partial<CreditCardDetailsFormValues> => ({
  name: values?.name ?? "",
});

export const getRestorableCreditCardFormValues = (
  values?: Partial<CreditCardFormValues> | null,
): Partial<CreditCardFormValues> => ({
  ...getRestorableCreditCardDetailsValues(values),
  billingAddress: values?.billingAddress
    ? {
        ...values.billingAddress,
      }
    : undefined,
});

export const detectCreditCardBrand = (
  cardNumber: string,
): CreditCardBrand | null => {
  const digits = cardNumber.replace(/\D/g, "");
  const prefix1 = Number(digits.slice(0, 1));
  const prefix2 = Number(digits.slice(0, 2));
  const prefix4 = Number(digits.slice(0, 4));

  if (prefix1 === 4) {
    return "visa";
  }

  if (prefix2 === 34 || prefix2 === 37) {
    return "amex";
  }

  if (
    (prefix2 >= 51 && prefix2 <= 55) ||
    (prefix4 >= 2221 && prefix4 <= 2720)
  ) {
    return "mastercard";
  }

  return null;
};

export const getDefaultBillingAddressValues = (
  locale = "en",
  countryCode = DEFAULT_COUNTRY_CODE,
): CreditCardBillingAddressValues => {
  return {
    country: getBillingAddressCountryOption(locale, countryCode),
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
  };
};

export const getDefaultCreditCardFormValues = (
  locale = "en",
  countryCode = DEFAULT_COUNTRY_CODE,
): CreditCardFormValues => ({
  ...getDefaultCreditCardDetailsValues(),
  billingAddress: getDefaultBillingAddressValues(locale, countryCode),
});

export const mergeCreditCardFormValues = (
  locale = "en",
  countryCode = DEFAULT_COUNTRY_CODE,
  values?: Partial<CreditCardFormValues> | null,
): CreditCardFormValues => {
  const defaultValues = getDefaultCreditCardFormValues(locale, countryCode);

  if (!values) {
    return defaultValues;
  }

  return {
    ...defaultValues,
    ...values,
    billingAddress: {
      ...defaultValues.billingAddress,
      ...(values.billingAddress || {}),
      country: values.billingAddress?.country?.value
        ? getBillingAddressCountryOption(
            locale,
            values.billingAddress.country.value,
          )
        : defaultValues.billingAddress.country,
    },
  };
};

export const createCreditCardValidationShape = (t: TFunction) => ({
  name: yup.string().required(t("error.requiredField")),
  number: yup.string().required(t("error.requiredField")),
  expiration: yup
    .string()
    .matches(/^\d{2}\/\d{4}$/, t("error.creditCard.wrongExpirationFormat"))
    .required(t("error.requiredField")),
  cvv: yup
    .string()
    .matches(
      /^\d{3,4}$/,
      t("error.numberLonger", {
        field: t("purchase.creditCard.cvv"),
        length: "3-4",
      }),
    )
    .required(t("error.requiredField")),
});

export const createBillingAddressValidationShape = (t: TFunction) => ({
  country: yup
    .object()
    .nullable()
    .required(
      t("error.required", {
        field: t("purchase.creditCard.countryRegion"),
      }),
    ),
  address1: yup
    .string()
    .trim()
    .required(
      t("error.required", {
        field: t("purchase.creditCard.address"),
      }),
    ),
  address2: yup.string().trim(),
  city: yup
    .string()
    .trim()
    .required(
      t("error.required", {
        field: t("auth.city"),
      }),
    ),
  state: yup
    .string()
    .trim()
    .required(
      t("error.required", {
        field: t("purchase.creditCard.state"),
      }),
    ),
  postalCode: yup
    .string()
    .trim()
    .required(
      t("error.required", {
        field: t("purchase.creditCard.zipCode"),
      }),
    ),
});

export const getBillingAddressPayload = (
  address: CreditCardBillingAddressValues,
): CreditCardBillingAddressDto => {
  const englishCountryName =
    (address.country?.value &&
      getCountryByCode(getCountriesOptions("en"), address.country.value)
        ?.label) ||
    address.country?.label ||
    "";

  return {
    billing_country: englishCountryName,
    billing_state: address.state.trim(),
    billing_city: address.city.trim(),
    billing_address_line_1: address.address1.trim(),
    billing_address_line_2: address.address2.trim(),
    billing_zip_code: address.postalCode.trim(),
  };
};
